import type { ChatMessage, ChatMessagePart, ChatCitation, ThreadNotice } from './chat-types';
// fork(oem): thread timestamps come from the frozen OEM clock, never the wall
// clock — the dataset's "now" is 09 Sep 2026 06:00 AEST and the thread sidebar
// is a snapshot target.
import { OEM_NOW_MS } from './oem-clock';

export type SeedThread = {
  id: string;
  title: string;
  pinned: boolean;
  updatedAt: number;
  messages: ChatMessage[];
  /** Set when the thread belongs to a project. The project-scoped chat
   *  sidebar (`/projects/<slug>/chat`) filters to these; the global `/`
   *  Pinned/Recents lists show every thread, matching ai-foundation. */
  projectSlug?: string;
};

const hour = 60 * 60 * 1000;

/**
 * fork(oem): one question and the answer the local engine gives for it.
 *
 * The four seeded threads below are **transcripts, not fiction**. Each body was
 * produced by running `shared/assistant/local-answer.ts` against the shipped
 * data pack and pasting the result, so a reader who asks the same question in
 * the rail gets the same figures back — the seed cannot drift from the engine
 * without someone noticing.
 *
 * Regenerate after a data-pack change by asking each thread's question in the
 * rail and copying the reply.
 */
function oemTurn(
  id: number,
  time: string,
  question: string,
  answer: string,
  tools: string,
  citations: ChatCitation[],
  /** fork(oem v1.8): parts after the markdown — e.g. a generated report card. */
  extra: ChatMessagePart[] = [],
): ChatMessage[] {
  return [
    { id, who: 'user', time, body: question },
    {
      id: id + 1,
      who: 'bot',
      time,
      body: answer,
      citations,
      parts: [
        { type: 'tool', verb: 'Ran', preview: tools },
        { type: 'markdown', content: answer },
        ...extra,
      ],
    },
  ];
}

/** Per-thread dismissible notices — keyed by thread id. */
export const SEED_THREAD_NOTICES: Record<string, ThreadNotice[]> = {
  // fork(oem): the foundation's connector-auth notice, retargeted at the OEM+
  // story — Kalbarri is the site with no live telematics, which is why the
  // L-02 answer reads off a shift-end export rather than a live feed.
  'thread-l02-cost': [
    {
      id: 'notice-kalbarri-shift-end',
      kind: 'connector-auth',
      severity: 'info',
      body: 'Kalbarri Gold has no live telematics — figures come from the Pitram shift-end export, read the morning after the shift.',
      actionHint: 'Open Integration → Connectors to see the feed and its latency.',
    },
  ],
};

/**
 * fork(oem): the four OEM+ threads.
 *
 * fork(oem v1.8): the foundation's six project-scoped threads ("Alpha kickoff notes" and the rest,
 * "seeded content for the rail history overlay") are gone. They showed in the
 * rail's history as generic filler, which is exactly what a reviewer asking
 * "are the answers pre-built and generic?" found. Every thread left is a real
 * engine transcript; the orphaned project pages render their empty state.
 */
export const SEED_THREADS: SeedThread[] = [
  {
    id: 'thread-l02-cost',
    title: 'L-02 coupling failure — what did it cost?',
    pinned: true,
    updatedAt: OEM_NOW_MS - 2 * hour,
    messages: oemTurn(
      1,
      '06:12',
      'What did the L-02 failure cost versus a planned change?',
      '**L-02 · Hydraulic pump drive coupling sheared** — Kalbarri Gold, 03 Sep 2026.\n\nThe failure cost **A$15,250** all-in (WO-26-11842: A$8,210 direct plus A$7,040 of lost production over 6.4 h down). The planned hydraulic pump & drive job against the same failure is **A$3,900** — a difference of **A$11,350**, 3.9× the cost of doing it on schedule.\n\nPrecursors, in order: **20 Aug** — Hydraulic oil temperature starts climbing in the shift-end export. **Aug** — 14 bucket-ramming events logged on L-02, 9 on Night B. **03 Sep 14:21** — Coupling shears. The agent chain replays the same fortnight on the shift-end export alone — open the incident to step through it.',
      '(local engine)',
      [{ label: 'incidents' }, { label: 'work_orders.csv' }, { label: 'rate card' }],
    ),
  },
  {
    id: 'thread-j04-confidence',
    title: 'J-04 front-end prediction — how confident?',
    pinned: true,
    updatedAt: OEM_NOW_MS - 5 * hour,
    messages: oemTurn(
      11,
      '06:34',
      'How confident should I be in the J-04 prediction?',
      // fork(oem v1.8): regenerated — the intent router answers this with the
      // alert's confidence and evidence, not the old risk-ranking table.
      '**J-04 · RH rock drill** — Rock drill front-end failure predicted (AL-0912, shift-end read, raised 08 Sep 07:12).\n\n**79% model confidence**, window **60–110 percussion h · 4–7 days at rehab duty** — enough to plan on it.\n\nWhat it rests on:\n- Pitram: percussion_pressure daily mean, 29 Aug – 8 Sep\n- Drill plan: 1840XC cut 212: RH holes 176 bar vs LH 194\n- Bulletin: TB-2026-027 §2 signature\n- Technician: Ben Tuilagi cut-change inspection 22:40 — water at front cover, one-sided shank wear\n\nDiagnostic agent: −8% percussion trend + pre-charge + shank overdue → 0.79, 60–110 percussion h.\n\nBeing wrong is cheap: the planned job is **A$19,848** against **A$60,717** if it fails first.',
      'alert_confidence(J-04) → (local engine)',
      [{ label: 'alerts' }, { label: 'alert evidence' }, { label: 'rate card' }],
    ),
  },
  {
    id: 'thread-loadhaul-weekly',
    title: 'Weekly status — Load & Haul',
    pinned: false,
    updatedAt: OEM_NOW_MS - 26 * hour,
    messages: oemTurn(
      21,
      '15:48',
      'Generate the weekly status report for Load & Haul',
      // fork(oem v1.8): regenerated — the report now builds (L3), so the old
      // "document generation is on the roadmap" reply was stale.
      '**Weekly status — Load & Haul** is open in the workspace — Week Mon 31 Aug – Sun 06 Sep 2026.\n\n- Availability 72.4–86.2% by mine; 2 breakdowns; A$182,076 direct + lost-production cost; 441 t CO₂-e.\n- 16 machines need attention — worst T-02 (Transmission 14/100).\n- 12 preventives due in the next 14 days; the largest order this week is WO-26-21625 on T-12.\n\nThe HTML file is ready to download from the card below.',
      'generate_report(loadhaul) → (local engine)',
      [{ label: 'generate_report' }],
      [
        {
          type: 'report',
          runId: 22,
          kind: 'loadhaul',
          title: 'Weekly status — Load & Haul',
          subtitle: 'Week Mon 31 Aug – Sun 06 Sep 2026',
          label: 'Weekly status · Load & Haul',
          format: 'docx',
          download: 'html',
          route: '/reports/weekly/loadhaul',
          steps: [
            { label: 'Condition register', detail: '623 components · 58 machines' },
            { label: 'Shift records', detail: '406 machine-days · week ending 06 Sep' },
            { label: 'SAP PM work orders', detail: '26 this week · 6 drafts' },
            { label: 'Energy', detail: 'diesel 2.68 kg/L · grid 3 site factors' },
            { label: 'Composing', detail: 'Weekly status · Load & Haul → HTML' },
          ],
        },
      ],
    ),
  },
  {
    id: 'thread-cost-per-hour',
    title: 'Cost per operating hour by mine',
    pinned: false,
    updatedAt: OEM_NOW_MS - 50 * hour,
    messages: oemTurn(
      31,
      '11:05',
      'Compare cost per operating hour by mine and explain the difference',
      '**Cost by mine** (18 months, all-in incl. lost production)\n\n| mine | Total | Labour | Parts | Freight | Lost production |\n|---|---|---|---|---|---|\n| Gawler Deep | A$7,698,269 | A$2,057,662 | A$2,867,898 | A$472,390 | A$2,249,619 |\n| Mount Isolde | A$4,084,537 | A$1,042,942 | A$1,451,700 | A$226,100 | A$1,332,445 |\n| Mount Read Metals | A$3,719,520 | A$953,388 | A$1,511,345 | A$232,220 | A$974,417 |\n| Kalbarri Gold | A$3,265,328 | A$673,718 | A$1,194,757 | A$61,480 | A$1,297,323 |\n| Broken Ridge | A$2,877,026 | A$729,736 | A$1,180,732 | A$144,040 | A$806,468 |\n| Ochre Hill Gold | A$1,135,190 | A$388,838 | A$394,604 | A$91,600 | A$246,998 |\n\nCost per operating hour: Kalbarri A$44, Broken Ridge A$28, Mount Isolde A$33. Kalbarri is highest despite the cheapest labour ($142/h) and easiest ground because reactive downtime is priced at A$1100/h for an LHD and its breakdowns run ~9 h. Most expensive per hour: L-02 (KG, A$56.6), L-03 (KG, A$56.5), L-01 (KG, A$51). Seismic exclusion downtime at Mount Isolde is coded to ground conditions, not to machines or Redgum (MI-SEIS-002).',
      'cost_summary(mine) → (local engine)',
      [{ label: 'cost_summary' }, { label: 'cost_ledger' }, { label: 'rate_card' }],
    ),
  },
];
